/**
 * UserPushDeviceService
 * Service for UserPushDevice entity operations
 * Route: api/userpushdevice
 */
import { EMPTY_BULK_DELETE_RESULT } from '../../models/common/BulkDelete';
import { CrudService } from '../base/CrudService';
/**
 * UserPushDeviceService
 * Handles push device CRUD operations
 */
export class UserPushDeviceService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UserPushDeviceService.DEFAULT_SERVICE_PATH,
            basePath: UserPushDeviceService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all devices with pagination
     * GET api/userpushdevice?page=1&pageSize=50
     */
    async getAll(params) {
        const response = await this.getAllRaw(params);
        return response.data ?? [];
    }
    /**
     * Get all devices with full response
     */
    getAllRaw(params) {
        const url = this.buildUrlWithParams(undefined, params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get device by ID
     * GET api/userpushdevice/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Device ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get device by ID with full response
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== GET BY USER ID ====================
    /**
     * Get all devices for a specific user
     * GET api/userpushdevice/user/{userId}
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get devices by user ID with full response
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('user', `user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== REGISTER DEVICE ====================
    /**
     * Register a new push device
     * POST api/userpushdevice
     */
    async register(data) {
        const response = await this.registerRaw(data);
        if (!response.data) {
            throw new Error('Failed to register device');
        }
        return response.data;
    }
    /**
     * Register device with full response
     */
    registerRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE DEVICE ====================
    /**
     * Update a push device
     * PUT api/userpushdevice/{id}
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error('Failed to update device');
        }
        return response.data;
    }
    /**
     * Update device with full response
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE DEVICE ====================
    /**
     * Delete a push device
     * DELETE api/userpushdevice/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete device with full response
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    /**
     * Delete many push devices in ONE request, instead of one call per ID.
     * IDs that no longer exist come back under notFoundIds rather than failing the call.
     * POST api/userpushdevice/bulk-delete
     */
    async deleteMany(ids) {
        const response = await this.deleteManyRaw(ids);
        return response.data ?? EMPTY_BULK_DELETE_RESULT;
    }
    /**
     * Bulk delete devices with full response
     */
    deleteManyRaw(ids) {
        const url = this.buildUrl('bulk-delete');
        return this.http.post(url, { ids });
    }
    // ==================== DEACTIVATE DEVICE ====================
    /**
     * Deactivate a push device
     * POST api/userpushdevice/{id}/deactivate
     */
    async deactivate(id) {
        const response = await this.deactivateRaw(id);
        return response.success;
    }
    /**
     * Deactivate device with full response
     */
    deactivateRaw(id) {
        const endpoint = this.getEndpoint('deactivate', `${id}/deactivate`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== ACTIVATE DEVICE ====================
    /**
     * Activate a push device
     * POST api/userpushdevice/{id}/activate
     */
    async activate(id) {
        const response = await this.activateRaw(id);
        return response.success;
    }
    /**
     * Activate device with full response
     */
    activateRaw(id) {
        const endpoint = this.getEndpoint('activate', `${id}/activate`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== MY DEVICES (Self-Service) ====================
    /**
     * Get all devices for the authenticated user
     * GET api/userpushdevice/my
     */
    async getMyDevices() {
        const response = await this.getMyDevicesRaw();
        return response.data ?? [];
    }
    /**
     * Get my devices with full response
     */
    getMyDevicesRaw() {
        const url = this.buildUrl('my');
        return this.http.get(url);
    }
    /**
     * Register a new device for the authenticated user
     * POST api/userpushdevice/my
     */
    async registerMyDevice(data) {
        const response = await this.registerMyDeviceRaw(data);
        if (!response.data) {
            throw new Error('Failed to register device');
        }
        return response.data;
    }
    /**
     * Register my device with full response
     */
    registerMyDeviceRaw(data) {
        const url = this.buildUrl('my');
        return this.http.post(url, data);
    }
    /**
     * Update a device for the authenticated user
     * PUT api/userpushdevice/my/{id}
     */
    async updateMyDevice(id, data) {
        const response = await this.updateMyDeviceRaw(id, data);
        if (!response.data) {
            throw new Error('Failed to update device');
        }
        return response.data;
    }
    /**
     * Update my device with full response
     */
    updateMyDeviceRaw(id, data) {
        const url = this.buildUrl(`my/${id}`);
        return this.http.put(url, data);
    }
    /**
     * Delete a device for the authenticated user
     * DELETE api/userpushdevice/my/{id}
     */
    async deleteMyDevice(id) {
        const response = await this.deleteMyDeviceRaw(id);
        return response.success;
    }
    /**
     * Delete my device with full response
     */
    deleteMyDeviceRaw(id) {
        const url = this.buildUrl(`my/${id}`);
        return this.http.delete(url);
    }
    /**
     * Deactivate a device for the authenticated user
     * POST api/userpushdevice/my/{id}/deactivate
     */
    async deactivateMyDevice(id) {
        const response = await this.deactivateMyDeviceRaw(id);
        return response.success;
    }
    /**
     * Deactivate my device with full response
     */
    deactivateMyDeviceRaw(id) {
        const url = this.buildUrl(`my/${id}/deactivate`);
        return this.http.post(url, {});
    }
    /**
     * Activate a device for the authenticated user
     * POST api/userpushdevice/my/{id}/activate
     */
    async activateMyDevice(id) {
        const response = await this.activateMyDeviceRaw(id);
        return response.success;
    }
    /**
     * Activate my device with full response
     */
    activateMyDeviceRaw(id) {
        const url = this.buildUrl(`my/${id}/activate`);
        return this.http.post(url, {});
    }
}
UserPushDeviceService.DEFAULT_SERVICE_PATH = 'users';
UserPushDeviceService.BASE_PATH = 'api/userpushdevice';
//# sourceMappingURL=UserPushDeviceService.js.map